import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './CorporateStructurePanel.css';

interface CompanyCandidate {
  legal_name: string;
  country?: string | null;
  city?: string | null;
  full_address?: string | null;
  website?: string | null;
  industry?: string | null;
  description?: string | null;
  confidence?: string;
}

interface CorporateEntity {
  legal_name: string;
  relationship: string;
  country?: string | null;
  city?: string | null;
  industry?: string | null;
  description?: string | null;
  ownership_pct?: string | null;
  confidence?: string;
}

interface CorporateStructureResult {
  root_entity: string;
  entities: CorporateEntity[];
  relationships: Array<{ parent: string; child: string; type: string }>;
  sources?: Array<{ title: string; url: string }>;
  notes?: string;
  error?: string;
  token_usage?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    estimated_cost_usd?: number;
  };
}

function safeExternalUrl(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

interface CorporateStructurePanelProps {
  structure: CorporateStructureResult;
  originalSelections: CompanyCandidate[];
  onConfirm: (selected: CompanyCandidate[], excluded: CompanyCandidate[]) => void;
  onSkip: () => void;
}

interface TreeNodeData {
  entity: CorporateEntity;
  children: TreeNodeData[];
}

const CorporateStructurePanel: React.FC<CorporateStructurePanelProps> = ({
  structure,
  originalSelections,
  onConfirm,
  onSkip,
}) => {
  const { isAdmin } = useAuth();
  const originalNames = useMemo(
    () => new Set(originalSelections.map((c) => c.legal_name)),
    [originalSelections]
  );

  const [selectedNames, setSelectedNames] = useState<Set<string>>(() => {
    const names: string[] = [];
    // Pre-check original selections
    originalSelections.forEach((c) => names.push(c.legal_name));
    // Pre-check SELF entities
    structure.entities.forEach((entity) => {
      if (entity.relationship === 'SELF' && names.indexOf(entity.legal_name) === -1) {
        names.push(entity.legal_name);
      }
    });
    return new Set<string>(names);
  });

  const toggleEntity = (name: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  // Original selections not present in structure entities (disambiguation picks the AI didn't rediscover)
  const orphanedOriginals = useMemo(() => {
    const structureNames = new Set(structure.entities.map((e) => e.legal_name));
    return originalSelections.filter((c) => !structureNames.has(c.legal_name));
  }, [structure.entities, originalSelections]);

  // All displayable entities: structure entities + orphaned originals converted to CorporateEntity shape
  const allDisplayEntities = useMemo(() => {
    const orphanEntities: CorporateEntity[] = orphanedOriginals.map((c) => ({
      legal_name: c.legal_name,
      relationship: 'SELF',
      country: c.country ?? undefined,
      city: c.city ?? undefined,
      industry: c.industry ?? undefined,
      description: c.description ?? undefined,
      confidence: 'HIGH',
    }));
    return [...structure.entities, ...orphanEntities];
  }, [structure.entities, orphanedOriginals]);

  const handleConfirm = () => {
    const selected: CompanyCandidate[] = [];
    const excluded: CompanyCandidate[] = [];

    for (const entity of allDisplayEntities) {
      const candidate: CompanyCandidate = {
        legal_name: entity.legal_name,
        country: entity.country,
        city: entity.city,
        industry: entity.industry,
        description: entity.description,
      };
      if (selectedNames.has(entity.legal_name)) {
        selected.push(candidate);
      } else {
        excluded.push(candidate);
      }
    }

    onConfirm(selected, excluded);
  };

  // Build tree from structure entities only (orphaned originals shown separately)
  const tree = useMemo(() => {
    return buildTree(structure);
  }, [structure]);

  const hasAffiliates = allDisplayEntities.filter(
    (e) => e.relationship !== 'SELF'
  ).length > 0;

  const selectedCount = selectedNames.size;
  const totalCount = allDisplayEntities.length;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedNames(new Set());
    } else {
      setSelectedNames(new Set(allDisplayEntities.map((e) => e.legal_name)));
    }
  };

  // No affiliates found — show single-node tree for main company
  if (!hasAffiliates && !structure.error) {
    const singleTree: TreeNodeData | null = tree || (structure.root_entity ? {
      entity: {
        legal_name: structure.root_entity,
        relationship: 'SELF',
        confidence: 'HIGH' as string,
      },
      children: [],
    } : null);

    return (
      <div className="corporate-structure-panel">
        <div className="structure-header">
          <h2>Corporate Structure</h2>
        </div>
        <div className="no-affiliates-message">
          <p>No corporate affiliates discovered for the selected companies.</p>
          {singleTree && (
            <div className="structure-tree" style={{ marginBottom: '16px' }}>
              <TreeNode
                node={singleTree}
                selectedNames={selectedNames}
                originalNames={originalNames}
                onToggle={toggleEntity}
              />
            </div>
          )}
          <button
            className="structure-confirm-btn"
            onClick={onSkip}
            type="button"
          >
            Proceed to Screening
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="corporate-structure-panel">
      <div className="structure-header">
        <h2>Corporate Structure</h2>
        <p className="structure-subtitle">
          We discovered the following corporate hierarchy. Select which entities
          to include in the compliance screening.
        </p>
        <p className="structure-hint">
          Originally selected companies are highlighted. Click entities to
          include or exclude them.
        </p>
        <p className="structure-caveat">
          This overview is limited to 50 entities and is derived from publicly
          available sources. While sourced from official filings and credible
          references where possible, corporate structures change frequently and
          this listing may not reflect the most current ownership or be fully
          comprehensive. Independent verification is recommended.
        </p>
      </div>

      <div className="select-all-row">
        <label className="select-all-label" onClick={toggleSelectAll}>
          <span className={`tree-card-checkbox${allSelected ? ' checked' : ''}`}>
            {allSelected && (
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path d="M13.5 4.5l-7 7L3 8" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </span>
          <span>Select All ({totalCount})</span>
        </label>
      </div>

      {/* Desktop: Tree view */}
      <div className="structure-tree-desktop">
        <div className="structure-tree">
          {tree && (
            <TreeNode
              node={tree}
              selectedNames={selectedNames}
              originalNames={originalNames}
              onToggle={toggleEntity}
            />
          )}
        </div>
      </div>

      {/* Mobile: Flat list */}
      <div className="structure-flat-mobile">
        <div className="structure-flat-list">
          {allDisplayEntities.map((entity) => {
            const isSelected = selectedNames.has(entity.legal_name);
            const isOriginal = originalNames.has(entity.legal_name);

            return (
              <div
                key={entity.legal_name}
                className={`flat-entity-item${isSelected ? ' selected' : ''}${isOriginal ? ' original' : ''}`}
                onClick={() => toggleEntity(entity.legal_name)}
              >
                <span
                  className={`tree-card-checkbox${isSelected ? ' checked' : ''}`}
                >
                  {isSelected && (
                    <svg
                      viewBox="0 0 16 16"
                      width="12"
                      height="12"
                      aria-hidden="true"
                    >
                      <path
                        d="M13.5 4.5l-7 7L3 8"
                        stroke="white"
                        strokeWidth="2.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <div className="flat-entity-content">
                  <h3 className="flat-entity-name">{entity.legal_name}</h3>
                  <div className="flat-entity-details">
                    <span
                      className={`relationship-type-badge ${entity.relationship.toLowerCase()}`}
                    >
                      {entity.relationship.replace('_', ' ')}
                    </span>
                    {(entity.city || entity.country) && (
                      <span className="tree-card-country">
                        {[entity.city, entity.country].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {entity.ownership_pct && (
                      <span className="tree-card-ownership">
                        {entity.ownership_pct}
                      </span>
                    )}
                  </div>
                  {entity.description && (
                    <p className="flat-entity-description">
                      {entity.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Orphaned originals: disambiguation picks not found in corporate hierarchy */}
      {orphanedOriginals.length > 0 && (
        <div className="orphaned-originals-section">
          <h4 className="orphaned-originals-heading">
            Additional Selections (not found in corporate hierarchy)
          </h4>
          <p className="orphaned-originals-note">
            These companies were selected during disambiguation but were not
            identified in the corporate structure above. They will still be
            included in screening if checked.
          </p>
          <div className="orphaned-originals-list">
            {orphanedOriginals.map((c) => {
              const isSelected = selectedNames.has(c.legal_name);
              return (
                <div
                  key={c.legal_name}
                  className={`flat-entity-item${isSelected ? ' selected' : ''} original`}
                  onClick={() => toggleEntity(c.legal_name)}
                >
                  <span
                    className={`tree-card-checkbox${isSelected ? ' checked' : ''}`}
                  >
                    {isSelected && (
                      <svg
                        viewBox="0 0 16 16"
                        width="12"
                        height="12"
                        aria-hidden="true"
                      >
                        <path
                          d="M13.5 4.5l-7 7L3 8"
                          stroke="white"
                          strokeWidth="2.5"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <div className="flat-entity-content">
                    <h3 className="flat-entity-name">{c.legal_name}</h3>
                    <div className="flat-entity-details">
                      <span className="relationship-type-badge self">
                        SELECTED
                      </span>
                      {c.country && (
                        <span className="tree-card-country">{c.country}</span>
                      )}
                    </div>
                    {c.description && (
                      <p className="flat-entity-description">{c.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {structure.notes && (
        <div className="structure-notes">{structure.notes}</div>
      )}

      <div className="structure-actions">
        <button
          className="structure-confirm-btn"
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          type="button"
        >
          Screen Selected
          {selectedCount > 0 && (
            <span className="structure-confirm-count">{selectedCount}</span>
          )}
        </button>
        <button
          className="structure-skip-btn"
          onClick={onSkip}
          type="button"
        >
          Skip &mdash; screen original selection only
        </button>
      </div>

      {structure.sources && structure.sources.length > 0 ? (
        <div className="structure-sources">
          <h4 className="structure-sources-heading">Sources</h4>
          <ul className="structure-sources-list">
            {structure.sources.map((source, idx) => {
              const href = source.url ? safeExternalUrl(source.url) : null;
              return (
                <li key={idx}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="structure-source-link"
                    >
                      {source.title || source.url}
                    </a>
                  ) : (
                    <span>{source.title}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : !structure.error && (
        <p className="structure-no-sources">
          Source links reflect web pages used during AI research. No specific sources were captured for this query.
        </p>
      )}

      {isAdmin && structure.token_usage && structure.token_usage.total_tokens > 0 && (
        <div className="structure-token-usage">
          <span className="structure-token-label">AI Tokens:</span>
          <span className="structure-token-value">
            {structure.token_usage.total_tokens.toLocaleString()}
          </span>
          <span className="structure-token-breakdown">
            ({structure.token_usage.input_tokens.toLocaleString()} in / {structure.token_usage.output_tokens.toLocaleString()} out)
          </span>
          {structure.token_usage.estimated_cost_usd != null && structure.token_usage.estimated_cost_usd > 0 && (
            <span className="structure-token-cost">
              Est. cost: ${structure.token_usage.estimated_cost_usd.toFixed(4)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/* ---- Tree building ---- */

function buildTree(structure: CorporateStructureResult): TreeNodeData | null {
  const { entities, relationships, root_entity } = structure;

  if (entities.length === 0) return null;

  // Map entity by name
  const entityMap = new Map<string, CorporateEntity>();
  for (const e of entities) {
    entityMap.set(e.legal_name, e);
  }

  // Build adjacency: parent -> children
  const childrenMap = new Map<string, string[]>();
  for (const rel of relationships) {
    const parent = rel.parent;
    const child = rel.child;
    if (!childrenMap.has(parent)) {
      childrenMap.set(parent, []);
    }
    childrenMap.get(parent)!.push(child);
  }

  // Find root
  let rootName = root_entity;
  if (!entityMap.has(rootName) && entities.length > 0) {
    // Fallback: find the PARENT entity, or first entity
    const parentEntity = entities.find((e) => e.relationship === 'PARENT');
    rootName = parentEntity ? parentEntity.legal_name : entities[0].legal_name;
  }

  // Build tree recursively with cycle detection
  const visited = new Set<string>();

  function buildNode(name: string): TreeNodeData | null {
    if (visited.has(name)) return null;
    visited.add(name);

    const entity = entityMap.get(name);
    if (!entity) return null;

    const childNames = childrenMap.get(name) || [];
    const children: TreeNodeData[] = [];
    for (const childName of childNames) {
      const childNode = buildNode(childName);
      if (childNode) {
        children.push(childNode);
      }
    }

    return { entity, children };
  }

  const tree = buildNode(rootName);

  // If tree only captured the root (orphan entities not in relationships),
  // attach them as direct children
  if (tree) {
    for (const entity of entities) {
      if (!visited.has(entity.legal_name)) {
        tree.children.push({ entity, children: [] });
      }
    }
  }

  return tree;
}

/* ---- Tree Node Component ---- */

interface TreeNodeProps {
  node: TreeNodeData;
  selectedNames: Set<string>;
  originalNames: Set<string>;
  onToggle: (name: string) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  selectedNames,
  originalNames,
  onToggle,
}) => {
  const { entity, children } = node;
  const isSelected = selectedNames.has(entity.legal_name);
  const isOriginal = originalNames.has(entity.legal_name);

  return (
    <div className="tree-node">
      <div
        className={`tree-node-card${isSelected ? ' selected' : ''}${isOriginal ? ' original' : ''}`}
        onClick={() => onToggle(entity.legal_name)}
      >
        <span
          className={`tree-card-checkbox${isSelected ? ' checked' : ''}`}
        >
          {isSelected && (
            <svg
              viewBox="0 0 16 16"
              width="12"
              height="12"
              aria-hidden="true"
            >
              <path
                d="M13.5 4.5l-7 7L3 8"
                stroke="white"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <div className="tree-card-content">
          <h3 className="tree-card-name">{entity.legal_name}</h3>
          <div className="tree-card-meta">
            <span
              className={`relationship-type-badge ${entity.relationship.toLowerCase()}`}
            >
              {entity.relationship.replace('_', ' ')}
            </span>
            {(entity.city || entity.country) && (
              <span className="tree-card-country">
                {[entity.city, entity.country].filter(Boolean).join(', ')}
              </span>
            )}
            {entity.ownership_pct && (
              <span className="tree-card-ownership">
                {entity.ownership_pct}
              </span>
            )}
          </div>
          {entity.description && (
            <p className="tree-card-description">{entity.description}</p>
          )}
        </div>
      </div>

      {children.length > 0 && (
        <div
          className={`tree-children${children.length > 1 ? ' multiple' : ''}`}
          style={
            children.length > 1
              ? ({ '--child-count': children.length } as React.CSSProperties)
              : undefined
          }
        >
          {children.map((child) => (
            <div key={child.entity.legal_name} className="tree-child-wrapper">
              <TreeNode
                node={child}
                selectedNames={selectedNames}
                originalNames={originalNames}
                onToggle={onToggle}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CorporateStructurePanel;
